import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Form, Formik, FormikHelpers } from 'formik';
import merge from 'lodash.merge';
import { DateTime } from 'luxon';

import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Dialog from '@material-ui/core/Dialog';

import { getYearsAndMonthsFromLuxon, sqlDateToYearAndMonth } from 'helpers/helpers'; //yearAndMonthToSql
import compose from 'helpers/compose';
import withoutEnterSubmit from 'components/withoutEnterSubmit';
import ControlButtons from 'components/ControlButtons';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import LoadingIndicator from 'components/Loading/LoadingIndicator';

import { ExternalProductsStore } from 'stores/workflowStores/finances/externalProducts/externalProducts';
import {
  getInitialValues,
  ExternalProductData,
  ExternalProductOptions,
  FinancialInstitution,
  ExtProductFreq,
  ExtProductUsage,
  ExtProductCategory,
} from './model';
import { Currency, AccountMode } from 'enums';
import ExternalProductModalSetup from './ExternalProductModalSetup';
import ExternalProductModalTermination from './ExternalProductModalTermination';

const FormWithoutEnterSubmit = withoutEnterSubmit(Form);

interface OwnProps {
  opened: boolean;
  handleClose: () => void;
  currency: Currency;
  productId?: string;
  category?: ExtProductCategory;
  refresh: () => void;
  options: ExternalProductOptions;
  defaultInflation: number;
  defaultInflationType: string;
  mode: AccountMode;
}

interface Stores {
  extProductsStore: ExternalProductsStore;
}

type Props = OwnProps & Stores;

enum Section {
  SETUP = 'SETUP',
  TERMINATION = 'TERMINATION',
}

const ExternalProductModal: React.FC<Props> = ({
  productId,
  category,
  opened,
  handleClose,
  extProductsStore,
  currency,
  refresh,
  options,
  defaultInflation,
  defaultInflationType,
  mode,
}) => {
  const { t } = useTranslation();
  let formSubmitting = false;
  const formKey = 'ExternalProductForm';
  const { fetchProduct, handleSubmit, data, invalidLink } = extProductsStore;
  const [section, setSection] = useState<Section>(Section.SETUP);
  const [model, setModel] = useState(false);
  const [nominal, setNominal] = useState(false);

  useEffect(() => {
    (async () => {
      if (opened && productId) {
        await fetchProduct(productId);
      }
    })();
  }, [productId, fetchProduct, opened]);

  const onSubmit = async (values: ExternalProductData, formikHelpers: FormikHelpers<ExternalProductData>) => {
    if (model) {
      await extProductsStore.model({ ...values, appliedValues: nominal });
      setModel(false);
    } else {
      const ok = await handleSubmit(
        { ...values, appliedValues: nominal },
        formikHelpers,
        section === Section.SETUP,
        //values.category === ExtProductCategory.LOAN,
        false,
      );
      if (ok) {
        if (section === Section.SETUP) {
          // && values.category !== ExtProductCategory.LOAN
          setSection(Section.TERMINATION);
        } else {
          handleClose();
          refresh();
        }
      }
    }
  };

  const minDate = useMemo(() => getYearsAndMonthsFromLuxon(DateTime.local().plus({ months: 1 })), []);
  const maxDate = useMemo(() => sqlDateToYearAndMonth(options?.maxDate), [options]);

  // FIXME: roll into one
  const initialValues = getInitialValues(currency);
  initialValues.inflation = { enabled: false, valuation: false, rate: defaultInflation, type: defaultInflationType };
  initialValues.category = category || ExtProductCategory.FINANCIAL_PRODUCT;
  initialValues.frequency = category === ExtProductCategory.PROPERTY ? ExtProductFreq.SINGLE : ExtProductFreq.MONTHLY;
  //initialValues.appliedValues = false;

  //console.log('redraw');

  const loading = () => (
    <div
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        minHeight: '8em',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'rgba(255,255,255,0.8)',
        zIndex: 1,
      }}
    >
      <LoadingIndicator size={56} />
    </div>
  );

  return (
    <>
      <Dialog maxWidth="md" open={opened}>
        <Formik
          initialValues={
            data || invalidLink
              ? merge(
                  {},
                  initialValues,
                  invalidLink
                    ? {
                        accountId: 'invalid',
                        invalidLink: true,
                        financialInstitution: { internal: true, title: 'Edward' },
                      }
                    : data,
                )
              : initialValues
          }
          onSubmit={onSubmit}
          enableReinitialize
        >
          {(formikProps) => {
            formSubmitting = formikProps.isSubmitting;

            const updateFinInst = (fi: FinancialInstitution | null) => {
              if (fi) {
                formikProps.setFieldValue('financialInstitution.title', fi.title);
                formikProps.setFieldValue('financialInstitution.id', fi.id);
                formikProps.setFieldValue('financialInstitution.internal', fi.internal);
              }
            };

            const { values } = formikProps;

            return (
              <>
                <DialogHeader onClose={handleClose}>
                  {t([`externalProduct_${section}_${values.category}`, `externalProduct_${section}`])}
                </DialogHeader>

                {extProductsStore.dataPrepared || !productId ? (
                  <DialogContent>
                    <FormWithoutEnterSubmit id={t(formKey)}>
                      {section === Section.SETUP && (
                        <ExternalProductModalSetup
                          productId={productId}
                          currency={currency}
                          options={options}
                          formikProps={formikProps}
                          updateFinInst={updateFinInst}
                          maxDate={maxDate}
                          setModel={setModel}
                          extProductsStore={extProductsStore}
                          nominal={nominal}
                          setNominal={setNominal}
                        />
                      )}
                      {section === Section.TERMINATION && (
                        <ExternalProductModalTermination
                          productId={productId}
                          currency={currency}
                          options={options}
                          formikProps={formikProps}
                          minDate={
                            formikProps.values.periodic?.until
                              ? getYearsAndMonthsFromLuxon(
                                  DateTime.fromSQL(formikProps.values.periodic.until).plus({ months: 1 }),
                                )
                              : minDate
                          }
                          maxDate={maxDate}
                          extProductsStore={extProductsStore}
                        />
                      )}
                    </FormWithoutEnterSubmit>
                    {extProductsStore.loading && loading()}
                  </DialogContent>
                ) : (
                  loading()
                )}
                <DialogActions>
                  {section === Section.SETUP ? (
                    <ControlButtons
                      key={'setup'}
                      isSubmitting={false}
                      nextText={t(
                        `externalProduct_TERMINATION${
                          values.category === ExtProductCategory.LOAN ? '_LOAN_short' : ''
                        }`,
                      )}
                      form={formKey}
                      backText={t('general_cancel')}
                      onBack={handleClose}
                      nextButtonId="ext-product-termination"
                      nextDisabled={values.invalidLink}
                    />
                  ) : (
                    <ControlButtons
                      key={'termination'}
                      isSubmitting={formSubmitting}
                      nextText={t('general_save')}
                      form={formKey}
                      backText={t(`externalProduct_SETUP_${values.category}`)}
                      onBack={() => setSection(Section.SETUP)}
                      nextButtonId="ext-product-save"
                      nextDisabled={
                        values.value.value === 0 &&
                        values.frequency !== ExtProductFreq.INFINITE &&
                        ((values.category !== ExtProductCategory.PROPERTY && values.usage !== ExtProductUsage.NONE) ||
                          (values.category === ExtProductCategory.PROPERTY && values.useTarget))
                      }
                    />
                  )}
                </DialogActions>
              </>
            );
          }}
        </Formik>
      </Dialog>
    </>
  );
};

export default compose(observer)(ExternalProductModal);
